import { useState, useEffect } from "react";
import { createClient } from "@supabase/supabase-js";
import AudioEngine from "./audioEngine";

const SUPABASE_URL = "https://trtieaijkrdipiljyzuh.supabase.co";
const SUPABASE_KEY = "sb_publishable_eJKNPdo5XwSCH-X8Ttantw_feBJed2q";
const supabase = createClient(SUPABASE_URL, SUPABASE_KEY);

const SYS = "#38bdf8";

const SYS_BORDER = "rgba(56,189,248,0.25)";
const WARN = "#ef4444";

function sanitizeName(val){ return (val||"").toUpperCase().replace(/[^A-Z0-9]/g,"").slice(0,25); }
function isValidName(val){ return /^[A-Z0-9]{3,25}$/.test((val||"").trim()); }

async function loadProfile(userId){
  try{
    const {data}=await supabase.from("profiles").select("*").eq("id",userId).single();
    return data;
  }catch{return null;}
}
async function saveProfile(userId,playerName,email){
  try{
    const row={id:userId,player_name:playerName};
    if(email) row.email=email;
    await supabase.from("profiles").upsert(row);
  }catch(e){console.error(e);}
}

// ─── Auth Screen ──────────────────────────────────────────────────────────────
export default function AuthScreen({onAuth}){
  const [mode,setMode]=useState("login"); // login | signup | verify | name
  const [email,setEmail]=useState("");
  const [password,setPassword]=useState("");
  const [playerName,setPlayerName]=useState("");
  const [loading,setLoading]=useState(false);
  const [error,setError]=useState("");
  const [nameAvail,setNameAvail]=useState(null); // null | "checking" | "taken" | "ok"

  const [signedUpUser,setSignedUpUser]=useState(null);
  const [premiumStatus,setPremiumStatus]=useState(null);
  const [premiumCheckLoading,setPremiumCheckLoading]=useState(false);
  const [showRequestForm,setShowRequestForm]=useState(false);
  const [requestNote,setRequestNote]=useState("");
  const [requestSent,setRequestSent]=useState(false);
  const [tempName,setTempName]=useState("");

  // On mount — check if user confirmed email and came back without a name
useEffect(()=>{
    supabase.auth.getSession().then(async({data:{session}})=>{
      if(session?.user){
        const profile=await loadProfile(session.user.id);
        const name=profile?.player_name;
        if(!name||!isValidName(name)){
          setSignedUpUser(session.user);
          setEmail(session.user.email||"");
          setMode("name");
        }
      }
    });
  },[]);

  async function checkNameAvailability(name){
    const n=sanitizeName(name);
    if(!n||n.length<3){setNameAvail(null);return;}
    setNameAvail("checking");
    const {data}=await supabase.from("profiles").select("id").eq("player_name",n).maybeSingle();
    setNameAvail(data?"taken":"ok");
  }

  async function checkPremiumName(name){
    const n=sanitizeName(name);
    if(!n){setPremiumStatus(null);return;}
    setPremiumCheckLoading(true);
    const {data}=await supabase.from("premium_usernames").select("*").eq("name",n).single();
    setPremiumCheckLoading(false);
    if(!data){setPremiumStatus(null);return;} // not premium
    if(!data.granted_to){setPremiumStatus("available");return;}
    let uid=signedUpUser?.id;
    if(!uid){const {data:{session}}=await supabase.auth.getSession();uid=session?.user?.id;}
    if(data.granted_to===uid){setPremiumStatus("owned_by_me");return;}
    setPremiumStatus("taken");
  }

  async function submitRequest(){
    if(!tempName.trim()){setError("Enter a temporary name first.");return;}
    setLoading(true);
    try{
      let u=signedUpUser;
      if(!u){const {data:{session}}=await supabase.auth.getSession();u=session?.user;}
      if(!u)throw new Error("Session expired.");
      // Save temp name first
      await saveProfile(u.id, sanitizeName(tempName), u.email);
      // Submit request
      await supabase.from("username_requests").insert({
        user_id:u.id,
        email:u.email,
        current_name:sanitizeName(tempName),
        requested_name:sanitizeName(playerName),
        note:requestNote.trim()||null,
        status:"pending",
      });
      setRequestSent(true);
    }catch(e){setError(e.message);}
    setLoading(false);
  }

  async function handleNameWithPremiumCheck(){
    // If premium and not owned by me — block direct set
    if(premiumStatus==="available"||premiumStatus==="taken"){
      setShowRequestForm(true);
      return;
    }
    handleSetName();
  }

  async function handleSubmit(){
    if(!email||!password){AudioEngine.error();setError("Enter email and password.");return;}
    setLoading(true);setError("");
    try{
      if(mode==="login"){
        const {data,error:e}=await supabase.auth.signInWithPassword({email,password});
        if(e){
          // Check if error is due to unconfirmed email — skip silently
          if(e.message?.toLowerCase().includes("confirm")||e.message?.toLowerCase().includes("not confirmed")||e.message?.toLowerCase().includes("email")){
            setError("Invalid email or password.");
          } else {
            throw e;
          }
          setLoading(false);
          return;
        }
       const profile=await loadProfile(data.user.id);
        const name=profile?.player_name;
        if(!name||!isValidName(name)){
          setSignedUpUser(data.user);
          setMode("name");
          setLoading(false);
          return;
        }
        setLoading(false);
        onAuth(data.user, name);
      } else {
        const {data,error:e}=await supabase.auth.signUp({email,password});
        if(e)throw e;
        // Clear any stale profile from previous signups with same email
        if(data.user) await supabase.from("profiles").delete().eq("id",data.user.id);
        setSignedUpUser(data.user);
        // Skip email verification — go straight to name setup
        setMode("name");
        setLoading(false);
        return;
      }
    }catch(e){AudioEngine.error();setError(e.message||"Something went wrong.");}
    setLoading(false);
  }

  async function handleGoogleSignIn(){
    setLoading(true);setError("");
    try{
      const {error:e}=await supabase.auth.signInWithOAuth({
        provider:"google",
        options:{redirectTo:window.location.origin},
      });
      if(e)throw e;
      // Browser navigates away to Google here — no further code runs
      // until the app reloads post-redirect, where getSession() picks up
      // the new session (handled in task-tracker.jsx and this file's mount effect).
    }catch(e){
      AudioEngine.error();
      setError(e.message||"Google sign-in failed. Please try again.");
      setLoading(false);
    }
  }

  async function handleSetName(){
    if(!isValidName(playerName)){AudioEngine.error();setError("Name must be 3–25 letters or numbers only.");return;}
    setLoading(true);setError("");
    try{
      let u=signedUpUser;
      if(!u){
        const {data:{session}}=await supabase.auth.getSession();
        u=session?.user;
      }
      if(!u)throw new Error("Session expired. Please sign up again.");
      await saveProfile(u.id, sanitizeName(playerName), u.email);
      onAuth(u, sanitizeName(playerName), true);
    }catch(e){AudioEngine.error();setError(e.message||"Something went wrong.");}
    setLoading(false);
  }

  return(
    <div style={{minHeight:"100vh",background:"#000",display:"flex",alignItems:"center",
      justifyContent:"center",padding:"20px",fontFamily:"'Outfit',sans-serif"}}>
      <div style={{width:"100%",maxWidth:380}}>
        {/* Logo */}
        <div style={{textAlign:"center",marginBottom:40}}>
          <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:28,fontWeight:800,
            color:SYS,textShadow:`0 0 30px ${SYS}`,letterSpacing:6,marginBottom:8}}>
            A.R.I.S.E
          </div>
          <div style={{fontFamily:"monospace",fontSize:10,color:"#64748b",letterSpacing:3}}>
            PLAYER SYSTEM
          </div>
        </div>

        {/* Card */}
        <div style={{background:"#030912",border:`1px solid ${SYS_BORDER}`,
          borderRadius:16,padding:"28px 24px"}}>
          
          <div style={{height:2,background:`linear-gradient(90deg,${SYS},transparent)`,
            borderRadius:2,marginBottom:24}}/>

          {mode==="verify"?(
            <>
              <div style={{fontFamily:"monospace",fontSize:11,color:SYS,letterSpacing:2,marginBottom:6}}>
                [ VERIFY YOUR EMAIL ]
              </div>
              <div style={{padding:"14px",background:"rgba(56,189,248,0.04)",
                border:`1px solid ${SYS_BORDER}`,borderRadius:10,marginBottom:20}}>
                <div style={{fontFamily:"monospace",fontSize:11,color:"#aaa",lineHeight:1.8}}>
                  Verification link sent to<br/>
                  <span style={{color:"#fff",fontWeight:700,fontFamily:"'Share Tech Mono',monospace",
                    letterSpacing:1}}>{email}</span>
                </div>
                <div style={{fontFamily:"monospace",fontSize:10,color:"#64748b",marginTop:10,lineHeight:1.7}}>
                  1. Check your inbox (and spam folder)<br/>
                  2. Click the confirmation link<br/>
                  3. Return here and log in
                </div>
              </div>
              {error&&<div style={{fontFamily:"monospace",fontSize:11,
                color:error.startsWith("✓")?"#4ade80":WARN,marginBottom:12}}>{error}</div>}
              <button onClick={async()=>{
                AudioEngine.click();
                await supabase.auth.resend({type:"signup",email});
                setError("✓ Verification email resent to "+email);
              }}
                style={{width:"100%",padding:"13px",background:"rgba(56,189,248,0.06)",
                  border:`1px solid ${SYS_BORDER}`,borderRadius:10,
                  color:"#aaa",fontWeight:600,fontSize:12,fontFamily:"monospace",
                  letterSpacing:2,cursor:"pointer",marginBottom:10}}>
                [ RESEND EMAIL ]
              </button>
              <button onClick={()=>{AudioEngine.click();setMode("login");}}
                style={{width:"100%",padding:"13px",background:"rgba(56,189,248,0.1)",
                  border:`1px solid ${SYS_BORDER}`,borderRadius:10,
                  color:SYS,fontWeight:800,fontSize:13,fontFamily:"monospace",letterSpacing:2,cursor:"pointer"}}>
                [ GO TO LOGIN ]
              </button>
            </>
          ):mode==="name"?(
            <>
              <div style={{fontFamily:"monospace",fontSize:11,color:SYS,letterSpacing:2,marginBottom:6}}>
                [ SET PLAYER NAME ]
              </div>
              <div style={{fontSize:12,color:"#64748b",marginBottom:20}}>
                This is how you'll appear in the system.
              </div>
              {requestSent?(
                // ── Request sent confirmation ──
                <div style={{textAlign:"center",padding:"10px 0"}}>
                  <div style={{fontSize:28,marginBottom:12}}>✦</div>
                  <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:13,color:"#4ade80",
                    letterSpacing:2,marginBottom:10}}>REQUEST SENT</div>
                  <div style={{fontFamily:"monospace",fontSize:11,color:"#64748b",lineHeight:1.7,marginBottom:20}}>
                    Your request for <span style={{color:"#4ade80",fontWeight:700}}>✦ {playerName}</span> is pending review.<br/>
                    You're now in the system as <span style={{color:"#fff",fontWeight:700}}>{tempName}</span>.<br/>
                    Once approved, you can change your name from settings.
                  </div>
                  <button onClick={()=>{AudioEngine.click();
                    let u=signedUpUser;
                    supabase.auth.getSession().then(({data:{session}})=>{
                      const usr=u||session?.user;
                      if(usr) onAuth(usr, tempName.trim(), true);
                    });
                  }}
                    style={{width:"100%",padding:"13px",background:SYS,border:"none",borderRadius:10,
                      color:"#000",fontWeight:800,fontSize:13,fontFamily:"monospace",letterSpacing:2,cursor:"pointer"}}>
                    [ ENTER SYSTEM ]
                  </button>
                </div>
              ):showRequestForm?(
                // ── Request form ──
                <div>
                  <div style={{padding:"12px 14px",background:"rgba(74,222,128,0.06)",
                    border:"1px solid rgba(74,222,128,0.25)",borderRadius:10,marginBottom:16}}>
                    <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:13,color:"#4ade80",
                      letterSpacing:2,marginBottom:4}}>✦ {playerName}</div>
                    <div style={{fontFamily:"monospace",fontSize:10,color:"#64748b"}}>
                      {premiumStatus==="taken"?"This name is reserved. Request it — the admin will review.":"This is a premium name. Request it — the admin will review."}
                    </div>
                  </div>
                  <div style={{fontFamily:"monospace",fontSize:10,color:"#64748b",letterSpacing:1,marginBottom:6}}>
                    SET A TEMPORARY NAME (while you wait)
                  </div>
                  <input value={tempName} onChange={e=>setTempName(sanitizeName(e.target.value))}
                    placeholder="TEMPORARY USERNAME..."
                    style={{width:"100%",background:"rgba(56,189,248,0.05)",border:`1px solid ${SYS_BORDER}`,
                      borderRadius:10,padding:"12px 14px",color:"#fff",fontSize:14,fontWeight:700,
                      fontFamily:"'Share Tech Mono',monospace",outline:"none",marginBottom:12,
                      letterSpacing:2,boxSizing:"border-box"}}/>
                  <div style={{fontFamily:"monospace",fontSize:10,color:"#64748b",letterSpacing:1,marginBottom:6}}>
                    NOTE TO ADMIN (optional)
                  </div>
                  <input value={requestNote} onChange={e=>setRequestNote(e.target.value.slice(0,120))}
                    placeholder="e.g. paid via GPay to 9999..."
                    style={{width:"100%",background:"rgba(56,189,248,0.05)",border:`1px solid ${SYS_BORDER}`,
                      borderRadius:10,padding:"11px 14px",color:"#fff",fontSize:12,
                      fontFamily:"monospace",outline:"none",marginBottom:16,boxSizing:"border-box"}}/>
                  {error&&<div style={{color:WARN,fontSize:11,fontFamily:"monospace",marginBottom:10}}>{error}</div>}
                  <button onClick={()=>{AudioEngine.click();submitRequest();}} disabled={loading}
                    style={{width:"100%",padding:"13px",background:"rgba(74,222,128,0.15)",
                      border:"1px solid rgba(74,222,128,0.4)",borderRadius:10,
                      color:"#4ade80",fontWeight:800,fontSize:13,fontFamily:"monospace",letterSpacing:2,
                      cursor:"pointer",marginBottom:10,opacity:loading?0.6:1}}>
                    {loading?"SUBMITTING...":"[ SEND REQUEST ]"}
                  </button>
                  <button onClick={()=>{AudioEngine.close();setShowRequestForm(false);}}
                    style={{width:"100%",padding:"11px",background:"rgba(255,255,255,0.03)",
                      border:"1px solid rgba(255,255,255,0.06)",borderRadius:10,
                      color:"#64748b",fontFamily:"monospace",fontSize:11,cursor:"pointer",letterSpacing:1}}>
                    ← BACK
                  </button>
                </div>
              ):(
                // ── Normal name entry ──
                <div>
                  <input value={playerName}
                    onChange={e=>{
                      const v=sanitizeName(e.target.value);
                      setPlayerName(v);
                      setPremiumStatus(null);
                      setShowRequestForm(false);
                      if(v.trim().length>=2){
                        clearTimeout(window._pnTimer);
                        window._pnTimer=setTimeout(()=>{checkPremiumName(v);checkNameAvailability(v);},400);
                      }
                    }}
                    onKeyDown={e=>e.key==="Enter"&&handleNameWithPremiumCheck()}
                    maxLength={25}
                    placeholder="ENTER YOUR NAME..."
                    style={{width:"100%",background:"rgba(56,189,248,0.05)",
                      border:`1px solid ${nameAvail==="taken"?"rgba(239,68,68,0.5)":nameAvail==="ok"?"rgba(74,222,128,0.4)":premiumStatus==="available"?"rgba(74,222,128,0.5)":premiumStatus==="taken"?"rgba(239,68,68,0.4)":SYS_BORDER}`,
                      borderRadius:10,padding:"12px 14px",color:"#fff",fontSize:15,fontWeight:700,
                      fontFamily:"'Share Tech Mono',monospace",outline:"none",marginBottom:6,
                      letterSpacing:2,boxSizing:"border-box"}}/>
                  {/* Name availability feedback */}
                  {nameAvail==="checking"&&(
                    <div style={{fontFamily:"monospace",fontSize:10,color:"#64748b",letterSpacing:1,marginBottom:8}}>
                      · · · CHECKING AVAILABILITY · · ·
                    </div>
                  )}
                  {nameAvail==="taken"&&(
                    <div style={{fontFamily:"monospace",fontSize:10,color:"#ef4444",letterSpacing:1,marginBottom:8}}>
                      ✗ NAME ALREADY TAKEN — CHOOSE ANOTHER
                    </div>
                  )}
                  {nameAvail==="ok"&&premiumStatus===null&&(
                    <div style={{fontFamily:"monospace",fontSize:10,color:"#4ade80",letterSpacing:1,marginBottom:8}}>
                      ✓ NAME IS AVAILABLE
                    </div>
                  )}
                  {nameAvail!=="checking"&&nameAvail!=="taken"&&nameAvail!=="ok"&&<div style={{marginBottom:8}}/>}
                  {/* Premium indicator */}
                  {premiumCheckLoading&&(
                    <div style={{fontFamily:"monospace",fontSize:10,color:"#64748b",marginBottom:10,letterSpacing:1}}>
                      · · · checking name · · ·
                    </div>
                  )}

                  {premiumStatus==="available"&&!premiumCheckLoading&&(
                    <div style={{borderRadius:12,overflow:"hidden",marginBottom:14}}>
                      {/* Header bar */}
                      <div style={{background:"linear-gradient(90deg,rgba(74,222,128,0.18),rgba(74,222,128,0.06))",
                        borderTop:"1px solid rgba(74,222,128,0.35)",borderLeft:"1px solid rgba(74,222,128,0.35)",
                        borderRight:"1px solid rgba(74,222,128,0.35)",
                        padding:"10px 14px",display:"flex",alignItems:"center",gap:10}}>
                        <span style={{fontSize:18}}>✦</span>
                        <div style={{flex:1}}>
                          <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:12,
                            color:"#4ade80",letterSpacing:3,fontWeight:700}}>PREMIUM NAME</div>
                          <div style={{fontFamily:"monospace",fontSize:9,color:"rgba(74,222,128,0.6)",
                            letterSpacing:1,marginTop:2}}>EXCLUSIVE · RESERVED</div>
                        </div>
                        <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:11,
                          color:"#4ade80",letterSpacing:2,fontWeight:700}}>AVAILABLE</div>
                      </div>
                      {/* Body */}
                      <div style={{background:"rgba(74,222,128,0.04)",
                        border:"1px solid rgba(74,222,128,0.2)",borderTop:"none",
                        padding:"12px 14px"}}>
                        <div style={{fontFamily:"monospace",fontSize:11,color:"#aaa",lineHeight:1.7,marginBottom:4}}>
                          <span style={{color:"#4ade80",fontWeight:700}}>✦ {playerName}</span> is a
                          premium name — exclusive to one player in the system.
                        </div>
                        <div style={{fontFamily:"monospace",fontSize:10,color:"#667",lineHeight:1.6}}>
                          This name is currently <span style={{color:"#4ade80",fontWeight:700}}>available</span>.
                          Request it and the admin will reach out to confirm payment.
                          While you wait, you'll play under a temporary name.
                        </div>
                      </div>
                    </div>
                  )}

                  {premiumStatus==="taken"&&!premiumCheckLoading&&(
                    <div style={{borderRadius:12,overflow:"hidden",marginBottom:14}}>
                      <div style={{background:"rgba(239,68,68,0.1)",
                        borderTop:"1px solid rgba(239,68,68,0.3)",borderLeft:"1px solid rgba(239,68,68,0.3)",
                        borderRight:"1px solid rgba(239,68,68,0.3)",
                        padding:"10px 14px",display:"flex",alignItems:"center",gap:10}}>
                        <span style={{fontSize:18}}>✦</span>
                        <div style={{flex:1}}>
                          <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:12,
                            color:WARN,letterSpacing:3,fontWeight:700}}>PREMIUM NAME</div>
                          <div style={{fontFamily:"monospace",fontSize:9,color:"rgba(239,68,68,0.6)",
                            letterSpacing:1,marginTop:2}}>EXCLUSIVE · RESERVED</div>
                        </div>
                        <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:11,
                          color:WARN,letterSpacing:2,fontWeight:700}}>TAKEN</div>
                      </div>
                      <div style={{background:"rgba(239,68,68,0.04)",
                        border:"1px solid rgba(239,68,68,0.15)",borderTop:"none",
                        padding:"12px 14px"}}>
                        <div style={{fontFamily:"monospace",fontSize:11,color:"#aaa",lineHeight:1.7,marginBottom:4}}>
                          <span style={{color:WARN,fontWeight:700}}>✦ {playerName}</span> is a
                          premium name already owned by another player.
                        </div>
                        <div style={{fontFamily:"monospace",fontSize:10,color:"#667",lineHeight:1.6}}>
                          You can still <span style={{color:"#f59e0b",fontWeight:700}}>send a request</span> — 
                          if the current owner gives it up or the admin decides otherwise, 
                          you'll be first in line.
                        </div>
                      </div>
                    </div>
                  )}

                  {premiumStatus==="owned_by_me"&&!premiumCheckLoading&&(
                    <div style={{padding:"10px 14px",background:"rgba(74,222,128,0.06)",
                      border:"1px solid rgba(74,222,128,0.25)",borderRadius:10,marginBottom:12,
                      display:"flex",alignItems:"center",gap:8}}>
                      <span>✦</span>
                      <div style={{fontFamily:"monospace",fontSize:10,color:"#4ade80",letterSpacing:1}}>
                        You already own this premium name — go ahead!
                      </div>
                    </div>
                  )}

                  {error&&<div style={{color:WARN,fontSize:11,fontFamily:"monospace",marginBottom:12}}>{error}</div>}

                  <button onClick={()=>{AudioEngine.click();handleNameWithPremiumCheck();}} disabled={loading}
                    style={{width:"100%",padding:"13px",
                      background:premiumStatus==="available"
                        ?"linear-gradient(135deg,rgba(74,222,128,0.2),rgba(74,222,128,0.1))"
                        :premiumStatus==="taken"
                        ?"rgba(245,158,11,0.12)"
                        :SYS,
                      border:premiumStatus==="available"
                        ?"1px solid rgba(74,222,128,0.45)"
                        :premiumStatus==="taken"
                        ?"1px solid rgba(245,158,11,0.35)"
                        :"none",
                      borderRadius:10,
                      color:premiumStatus==="available"?"#4ade80"
                        :premiumStatus==="taken"?"#f59e0b"
                        :"#000",
                      fontWeight:800,fontSize:13,fontFamily:"monospace",letterSpacing:2,
                      cursor:"pointer",opacity:loading?0.6:1}}>
                    {loading?"CHECKING..."
                      :premiumStatus==="available"?"[ ✦ BUY THIS NAME ]"
                      :premiumStatus==="taken"?"[ REQUEST ANYWAY ]"
                      :"[ ENTER SYSTEM ]"}
                  </button>
                </div>
              )}
            </>
          ):(
            <>
              <div style={{fontFamily:"monospace",fontSize:11,color:SYS,letterSpacing:2,marginBottom:20}}>
                {mode==="login"?"[ PLAYER LOGIN ]":"[ CREATE ACCOUNT ]"}
              </div>

              <button onClick={()=>{AudioEngine.click();handleGoogleSignIn();}} disabled={loading}
                style={{width:"100%",padding:"12px",background:"rgba(255,255,255,0.04)",
                  border:"1px solid rgba(255,255,255,0.12)",borderRadius:10,
                  color:"#fff",fontWeight:600,fontSize:13,fontFamily:"'Outfit',sans-serif",
                  cursor:"pointer",opacity:loading?0.6:1,marginBottom:16,
                  display:"flex",alignItems:"center",justifyContent:"center",gap:10}}>
                <svg width="16" height="16" viewBox="0 0 48 48">
                  <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.1 8 3l6-6C34.5 5.1 29.5 3 24 3 12.4 3 3 12.4 3 24s9.4 21 21 21 21-9.4 21-21c0-1.4-.1-2.7-.4-3.5z"/>
                  <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 15.9 18.9 13 24 13c3.1 0 5.8 1.1 8 3l6-6C34.5 5.1 29.5 3 24 3 16.3 3 9.7 7.3 6.3 14.7z"/>
                  <path fill="#4CAF50" d="M24 45c5.4 0 10.3-1.8 14-5.7l-6.6-5.4c-2 1.5-4.6 2.4-7.4 2.4-5.3 0-9.7-3.3-11.3-8l-6.6 5.1C9.6 40.6 16.2 45 24 45z"/>
                  <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.2 4.2-4 5.6l6.6 5.4C41.5 36 45 30.6 45 24c0-1.4-.1-2.7-.4-3.5z"/>
                </svg>
                Continue with Google
              </button>

              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16}}>
                <div style={{flex:1,height:1,background:"rgba(255,255,255,0.08)"}}/>
                <div style={{fontFamily:"monospace",fontSize:9,color:"#64748b",letterSpacing:1}}>OR</div>
                <div style={{flex:1,height:1,background:"rgba(255,255,255,0.08)"}}/>
              </div>

              <div style={{display:"flex",flexDirection:"column",gap:12,marginBottom:16}}>
                <input value={email} onChange={e=>setEmail(e.target.value)}
                  placeholder="Email" type="email"
                  style={{background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.08)",
                    borderRadius:10,padding:"12px 14px",color:"#fff",fontSize:14,outline:"none",
                    fontFamily:"'Outfit',sans-serif"}}/>
                <input value={password} onChange={e=>setPassword(e.target.value)}
                  onKeyDown={e=>e.key==="Enter"&&handleSubmit()}
                  placeholder="Password" type="password"
                  style={{background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.08)",
                    borderRadius:10,padding:"12px 14px",color:"#fff",fontSize:14,outline:"none",
                    fontFamily:"'Outfit',sans-serif"}}/>
              </div>
              {error&&<div style={{color:WARN,fontSize:11,fontFamily:"monospace",marginBottom:12}}>{error}</div>}
              <button onClick={()=>{AudioEngine.click();handleSubmit();}} disabled={loading}
                style={{width:"100%",padding:"13px",background:SYS,border:"none",borderRadius:10,
                  color:"#000",fontWeight:800,fontSize:13,fontFamily:"monospace",letterSpacing:2,
                  cursor:"pointer",opacity:loading?0.6:1,marginBottom:16}}>
                {loading?"...":(mode==="login"?"[ LOGIN ]":"[ SIGN UP ]")}
              </button>
              <div style={{textAlign:"center",fontFamily:"monospace",fontSize:11,color:"#64748b"}}>
                {mode==="login"?"No account? ":"Have an account? "}
                <span onClick={()=>{AudioEngine.click();setMode(mode==="login"?"signup":"login");setError("");}}
                  style={{color:SYS,cursor:"pointer",textDecoration:"underline"}}>
                  {mode==="login"?"Sign up":"Login"}
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


